import type { ValueJob } from '@/util/vexo/orderValue';

import type { CreateOrderPayload } from './buildOrderPayload';

// Mirrors V2's buildOrderValueJobsFromState (CreateOrderV2/api/buildCreatePayload.ts)
// so both order flows feed the same computeOrderTotalValue. pay_rate is in cents.
// Kept as its own type-only module so the value math is unit-testable without
// importing buildOrderPayload's runtime context deps, which pull in the App/Ionic
// tree and fail to load under Jest.
//
// Two intentional divergences from the V2 adapter; both are immaterial to an
// analytics estimate:
//   1. Duration: V1 measures elapsed time from the ISO instants (DST-sensitive),
//      whereas V2 uses a wall-clock HH:MM diff (DST-agnostic). They differ only
//      for a shift that crosses a DST boundary. V1 can't reuse V2's approach
//      because the payload carries full ISO instants, not the sliceable format.
//   2. Break source: the V1 payload only has a job-level mandatory_break_time;
//      it has no per-shift break_minutes like V2 (shift.break_minutes ?? job…).
export function buildOrderValueJobsFromPayload(
  payload: CreateOrderPayload,
): ValueJob[] {
  return payload.jobs.map((job) => ({
    payRateDollars: (job.pay_rate ?? 0) / 100,
    shifts: (job.shifts ?? []).map((shift) => {
      // Guard malformed shift dates: Date.parse -> NaN would propagate to a
      // NaN total_value, so coerce a non-finite duration to 0.
      const grossMinutes =
        (Date.parse(shift.end_at) - Date.parse(shift.start_at)) / 60000;
      return {
        grossMinutes: Number.isFinite(grossMinutes)
          ? Math.max(0, grossMinutes)
          : 0,
        breakMinutes: job.mandatory_break_time ?? 0,
      };
    }),
  }));
}
